import { LoadingButton } from "@mui/lab";
import { Button, CircularProgress, Container, Stack } from "@mui/material";
import { useToasterContext } from "@src/context/Toaster";
import { AlertType } from "@src/lib/types/toaster";
import Config from "@src/utils/api/config";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import CurrentDraw from "./current-draw";
import DescriptionDialog from "./description-dialog";
import { useServices } from "./hooks";
import Input from "./input";
import LastweekDraw from "./lastweek-draw";
import Provider, { useDrawResultContext } from "./provider";
import { StyledTypography, StyledUploadBox } from "./styles.css";
import SuccessDialog from "./success";
import UploadMask from "./upload-mask";
import PreviousDraw from "./previous-draw";
import ViewWinners from "./view-winners";

export default function DrawResultPage() {
	return (
		<Provider>
		<>
			<Helmet>
				<title> Raffle Draw Results </title>
			</Helmet>
      <Component />
      <DescriptionDialog />
      <SuccessDialog />
		</>
		</Provider>
	)
}

function Component() {
	const { uploadImage } = useServices();
	const { triggerOpen } = useToasterContext();
	const { state, error, onDispatch, setContainer, isLoading, isDirty, onSubmit } = useDrawResultContext();

	const handleFileChange = (type: string) => (event: any) => {
		const file = event.target.files[0];

		if (file) {
			if (file.size > 3 * 1024 * 1024) {
				triggerOpen({
					type: AlertType.error,
					title: "Failed",
					message: "File size must be less than 3MB",
				});
				return;
			}

			const formData = new FormData();
			formData.append("file", file);
			formData.append("type", type);
			uploadImage.mutate(formData);
		}
	};

	useEffect(() => {
		if(uploadImage.data && uploadImage.isSuccess){
			onDispatch(uploadImage.data.type)(`${Config.APP_CDN_URL}/${uploadImage.data.result.filePath}`);   
		}
	},[uploadImage.isSuccess, uploadImage.data, onDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Container maxWidth="xl" >
			<Stack sx={{width: "100%", }}>
				<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"auto"}>
					<StyledTypography >Main Banner</StyledTypography>
					<LoadingButton
						onClick={onSubmit}
						loading={isLoading}
						disabled={!isDirty}
						sx={{ width: 145,
							height: "50px", borderRadius: 1,
							color: "white", bgcolor: isDirty ? "#8F17D9" : "#484848",
							alignSelf: "center",
							"&:hover": { bgcolor: isDirty ? "#7a12bb" : "#333" },
						}} >
						PUBLISH
					</LoadingButton>
				</Stack>
				<StyledUploadBox sx={[{mt: 4 }, error.image && {border: "1px dashed red"}]} >
					<input
						type="file"
						id="image"
						accept="image/*"
						style={{ display: 'none' }}
						onChange={handleFileChange("image")}
					/>
					{!state.image && <UploadMask notes="Recommended size: 1085x300" 
						notesSx={{fontSize: 9}}
						notes2Sx={{fontSize: 9}}
						notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
					{uploadImage.isPending && 
						<CircularProgress sx={{position: "absolute"}} />}
					{state.image && 
						<img 
							src={state.image} 
							alt="Selected" 
							style={{ maxWidth: '90%', height: '90%' }} />}
				</StyledUploadBox>
				<Button  onClick={() => {
					document.getElementById('image')?.click();
				}} sx={{ width: 230, 
					height: 40, borderRadius: 1, 
					color: "white", bgcolor: "#484848", 
					alignSelf: "center", marginTop: 3}} >UPLOAD IMAGE</Button>
				<Stack>
					<Input 
						disabled
						label="Draw Description"
						labelSx={{
							fontSize: "20px",
							marginBottom: 16
						}}
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								minHeight: '98px',
							},
							"& .MuiOutlinedInput-root": {
								alignItems: "flex-start",
								padding: "12px 14px",           // 👈 Better paragraph feel
							},
							"& .MuiOutlinedInput-input": {
								fontSize: "15px",
								lineHeight: "22px",
								padding: 0,                     // removes default tight padding
							},
							"& .MuiInputBase-root.Mui-disabled": {
								opacity: 1,
								color: "black",
							},
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "black", // required for Safari
							},
						}}
						placeholder='Description...'
						error={error.description}
						value={state.description}
						onChange={onDispatch("description")} 
						/>
					<Button  onClick={() => setContainer("description")} sx={{ width: 230, 
						height: 40, borderRadius: 1, 
						color: "white", bgcolor: "#484848", 
						alignSelf: "center", marginTop: 3}} >
						EDIT DRAW DESCRIPTION
					</Button>
				</Stack>
				<CurrentDraw />
				<LastweekDraw />
				<PreviousDraw />
				<ViewWinners />
			</Stack>
		</Container>
	)
}